import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Loader2, Film, Tv, User, ListVideo, Users, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPosterUrl } from '../utils/helpers';
import {
  useSaveSearchHistoryMutation,
} from '../hooks/useSearchHistoryQuery';
import {
  useRecentSearches,
  useSaveRecentSearchMutation,
  type RecentSearchItem,
  getLocalRecents,
  saveLocalRecent,
} from '../hooks/useRecentSearchesQuery';
import {
  useSearchAll,
  getResultRoute,
  type UnifiedSearchResult,
  type EntityType,
} from '../hooks/useSearchAllQuery';
import type { MediaType } from '../types/movie';

const MAX_SEARCH_QUERY_LENGTH = 500;

// Helper to get icon component for each entity type
function getEntityIcon(entityType: EntityType) {
  switch (entityType) {
    case 'movie':
      return Film;
    case 'tv':
      return Tv;
    case 'person':
      return User;
    case 'user':
      return Users;
    case 'list':
      return ListVideo;
    default:
      return Film;
  }
}

// Helper to get person role label from known_for_department
function getPersonRoleLabel(knownFor: string | undefined | null): string {
  if (!knownFor) return 'Person';
  const dept = knownFor.toLowerCase();
  if (dept === 'acting') return 'Actor';
  if (dept === 'directing') return 'Director';
  if (dept === 'writing') return 'Writer';
  return 'Person';
}

// Helper to get badge label (handles person role labels)
function getBadgeLabel(entityType: EntityType, meta?: Record<string, unknown>): string {
  if (entityType === 'person') {
    const knownFor = meta?.known_for as string | undefined;
    return getPersonRoleLabel(knownFor);
  }
  switch (entityType) {
    case 'movie': return 'Movie';
    case 'tv': return 'TV';
    case 'user': return 'User';
    case 'list': return 'List';
    default: return '';
  }
}

// Helper to get badge color classes (movie/TV match MediaToggle gradients)
function getBadgeColorClass(entityType: EntityType): string {
  switch (entityType) {
    case 'movie': return 'bg-gradient-to-r from-cyan-500 to-teal-500';
    case 'tv': return 'bg-gradient-to-r from-purple-500 to-pink-500';
    case 'person': return 'bg-green-600';
    case 'user': return 'bg-cyan-600';
    case 'list': return 'bg-orange-600';
    default: return 'bg-slate-600';
  }
}

interface SearchAutocompleteProps {
  mediaType: MediaType;
  onSearch: (query: string) => void;
  searchQuery: string;
}
export function SearchAutocomplete({ mediaType, onSearch, searchQuery }: SearchAutocompleteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const isLoggedIn = !!session?.access_token;
  const saveSearchHistoryMutation = useSaveSearchHistoryMutation();
  const recentSearchesQuery = useRecentSearches();
  const saveRecentSearchMutation = useSaveRecentSearchMutation();
  const [guestRecents, setGuestRecents] = useState<RecentSearchItem[]>(() => getLocalRecents());
  const recentItems: RecentSearchItem[] = isLoggedIn ? (recentSearchesQuery.data ?? []) : guestRecents;
  const loadingRecentItems = isLoggedIn
    ? (recentSearchesQuery.isLoading && !recentSearchesQuery.data)
    : false;

  const [inputValue, setInputValue] = useState(searchQuery);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedQueryRef = useRef<{ query: string; time: number } | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Unified search using TanStack Query
  const searchQuery_ = useSearchAll(debouncedQuery, { limit: 8 });
  const suggestions = searchQuery_.data?.results ?? [];
  const isLoading = searchQuery_.isFetching && debouncedQuery.length >= 2;

  // Prevent stale suggestions flash: only show suggestions when debounced query matches input
  const queryMatches = debouncedQuery.trim() === inputValue.trim();


  // Update dropdown position when showing
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update position BEFORE paint when dropdown opens or layout changes (route/mediaType)
  useLayoutEffect(() => {
    if (!showDropdown) return;
    updateDropdownPosition();
  }, [showDropdown, updateDropdownPosition, location.pathname, mediaType]);

  // Register scroll/resize/sidebar-collapse listeners when dropdown is visible
  useEffect(() => {
    if (showDropdown) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('sidebar-collapse-change', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('sidebar-collapse-change', updateDropdownPosition);
      };
    }
  }, [showDropdown, updateDropdownPosition]);

  // Sync input value with external searchQuery
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Close dropdown when clicking outside (check BOTH container AND portaled dropdown)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);

      if (!inContainer && !inDropdown) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveSearchQuery = useCallback((query: string) => {
    if (!isLoggedIn) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    // Match server-side normalization (NFC + trim + lowercase + collapse internal whitespace)
    const normalized = trimmed.normalize('NFC').toLowerCase().replace(/\s+/g, ' ');
    const now = Date.now();
    if (lastSavedQueryRef.current?.query === normalized && now - lastSavedQueryRef.current.time < 2000) {
      return;
    }
    lastSavedQueryRef.current = { query: normalized, time: now };
    saveSearchHistoryMutation.mutate(trimmed);
  }, [isLoggedIn, saveSearchHistoryMutation]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, MAX_SEARCH_QUERY_LENGTH);
    setInputValue(value);
    setShowDropdown(true);

    // Debounce the query update (TanStack Query handles the actual fetching)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim().slice(0, MAX_SEARCH_QUERY_LENGTH);
    saveSearchQuery(trimmed);
    setShowDropdown(false);
    onSearch(trimmed);
  };

  const handleSuggestionClick = (item: UnifiedSearchResult) => {
    const route = getResultRoute(item);
    if (!route) {
      // Cannot navigate (e.g., user without username) - just close dropdown
      setShowDropdown(false);
      return;
    }
    setShowDropdown(false);
    setInputValue('');
    setDebouncedQuery('');
    onSearch('');
    const meta = (item.meta as Record<string, unknown>) || {};
    const rawYear = (meta as { year?: unknown }).year;
    const year = typeof rawYear === 'number' ? rawYear : null;
    const recentItem: RecentSearchItem = {
      id: String(item.id),
      media_type: item.entity_type,
      title: item.title,
      poster_path: (meta as { poster_path?: string | null })?.poster_path ?? null,
      image_url: item.image_url,
      subtitle: item.subtitle || (year ? String(year) : null),
      year,
      // Keep full metadata payload (including user `username`) for route reconstruction.
      meta,
    };
    if (isLoggedIn) {
      saveRecentSearchMutation.mutate(recentItem);
    } else {
      setGuestRecents(saveLocalRecent(recentItem));
    }
    navigate(route);
  };

  const handleRecentItemClick = (item: RecentSearchItem) => {
    const entityType = item.media_type as EntityType;
    const resultLike: UnifiedSearchResult = {
      entity_type: entityType,
      id: item.id,
      title: item.title,
      subtitle: item.subtitle ?? '',
      image_url: item.image_url,
      score: 0,
      match: {
        title_highlight: item.title,
        subtitle_highlight: item.subtitle ?? '',
        reasons: [],
        match_strength: 0,
      },
      meta: (item.meta as Record<string, unknown>) || {},
    };

    const route = getResultRoute(resultLike);
    if (!route) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(false);
    setInputValue('');
    setDebouncedQuery('');
    onSearch('');

    if (isLoggedIn) {
      saveRecentSearchMutation.mutate(item);
    } else {
      setGuestRecents(saveLocalRecent(item));
    }

    navigate(route);
  };

  const handleFocus = () => {
    if (inputValue.length >= 2 && suggestions.length > 0) {
      setShowDropdown(true);
    } else if (inputValue.length < 2) {
      setShowDropdown(true);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1 max-w-xl">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            maxLength={MAX_SEARCH_QUERY_LENGTH}
            placeholder="Search movies, shows, actors, users & lists..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-700 border border-slate-600 rounded-full text-slate-200 placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
          />
          {isLoading && (
            <Loader2
              size={18}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"
            />
          )}
        </div>
      </form>

      {/* Autocomplete dropdown - portaled to body for proper z-index stacking */}
      {showDropdown && (inputValue.length >= 2 || (inputValue.length < 2 && (recentItems.length > 0 || loadingRecentItems))) && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-xl shadow-xl shadow-black/40 z-40 overflow-hidden"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          {inputValue.length >= 2 && queryMatches && suggestions.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              {suggestions.map((item) => {
                const badgeLabel = getBadgeLabel(item.entity_type, item.meta as Record<string, unknown>);
                const badgeColorClass = getBadgeColorClass(item.entity_type);
                const EntityIcon = getEntityIcon(item.entity_type);
                const visibility = item.entity_type === 'user' ? String((item.meta as {visibility?: unknown})?.visibility ?? '') : '';
                const isLockedUser = item.entity_type === 'user' && visibility !== '' && visibility !== 'public';
                return (
                  <button
                    key={`${item.entity_type}-${item.id}`}
                    onClick={() => handleSuggestionClick(item)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-700/50 transition-colors text-left"
                  >
                    {/* Thumbnail */}
                    <div className={`flex-shrink-0 overflow-hidden bg-slate-700 ${item.entity_type === 'user' ? 'w-11 h-11 rounded-full' : 'w-10 h-14 rounded'}`}>
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">
                          <EntityIcon size={16} />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Title (plain text, no highlighting) */}
                        <span className="text-white font-medium truncate">
                          {item.title}
                        </span>
                        {/* Entity type badge (smaller, movie/TV use MediaToggle gradients) */}
                        <span className={`text-[8px] ${badgeColorClass} text-white px-1 py-0.5 rounded font-semibold`}>
                          {badgeLabel}
                        </span>
                        {/* Lock icon for private profile users */}
                        {isLockedUser && (
                          <span title="Private profile">
                            <Lock size={12} className="text-slate-400" />
                          </span>
                        )}
                      </div>
                      {/* Subtitle from API */}
                      <div className="text-xs text-slate-400 truncate">
                        {item.entity_type === 'user' ? (() => {
                          const meta = item.meta as Record<string, unknown> | undefined;
                          const hasCountData = meta?.followers_count != null || meta?.following_count != null;
                          if (hasCountData) {
                            return (
                              <>
                                <span>@{String(meta?.username ?? '')}</span>
                                <span className="mx-1">&middot;</span>
                                <span>{Number(meta?.followers_count ?? 0)} followers</span>
                                <span className="mx-1">&middot;</span>
                                <span>{Number(meta?.following_count ?? 0)} following</span>
                              </>
                            );
                          }
                          return item.subtitle || `@${String(meta?.username ?? '')}`;
                        })() : (
                          item.subtitle
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : inputValue.length >= 2 && queryMatches && !isLoading ? (
            <div className="p-4 text-center text-slate-400">
              No results found for "{inputValue}"
            </div>
          ) : null}

          {/* Recent entity searches (when input is 0-1 chars) */}
          {inputValue.length < 2 && (
            recentItems.length > 0 ? (
              <>
                <div className="px-3 py-2 text-xs text-slate-500 uppercase tracking-wider font-medium border-t border-white/[0.08]">
                  Recent
                </div>
                {recentItems.map((item) => {
                  const entityType = item.media_type as EntityType;
                  const badgeLabel = getBadgeLabel(entityType, item.meta as Record<string, unknown>);
                  const badgeColorClass = getBadgeColorClass(entityType);
                  const EntityIcon = getEntityIcon(entityType);
                  const visibility = entityType === 'user' ? String((item.meta as {visibility?: unknown})?.visibility ?? '') : '';
                  const isLockedUser = entityType === 'user' && visibility !== '' && visibility !== 'public';
                  const imageUrl = item.image_url || (item.poster_path ? getPosterUrl(item.poster_path, 'small') : null);
                  return (
                    <button
                      key={`recent-entity-${item.media_type}-${item.id}`}
                      type="button"
                      onClick={() => handleRecentItemClick(item)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-700/50 transition-colors text-left"
                    >
                      <div className={`flex-shrink-0 overflow-hidden bg-slate-700 ${item.media_type === 'user' ? 'w-11 h-11 rounded-full' : 'w-10 h-14 rounded'}`}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-500">
                            <EntityIcon size={16} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium truncate">
                            {item.title}
                          </span>
                          <span className={`text-[8px] ${badgeColorClass} text-white px-1 py-0.5 rounded font-semibold`}>
                            {badgeLabel}
                          </span>
                          {isLockedUser && (
                            <span title="Private profile">
                              <Lock size={12} className="text-slate-400" />
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.media_type === 'user' ? (() => {
                            const meta = item.meta as Record<string, unknown> | undefined;
                            const hasCountData = meta?.followers_count != null || meta?.following_count != null;
                            if (hasCountData) {
                              return (
                                <>
                                  <span>@{String(meta?.username ?? '')}</span>
                                  <span className="mx-1">&middot;</span>
                                  <span>{Number(meta?.followers_count ?? 0)} followers</span>
                                  <span className="mx-1">&middot;</span>
                                  <span>{Number(meta?.following_count ?? 0)} following</span>
                                </>
                              );
                            }
                            return item.subtitle || `@${String(meta?.username ?? '')}`;
                          })() : (
                            item.subtitle || (item.year ? String(item.year) : null)
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </>
            ) : loadingRecentItems ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                <Loader2 size={16} className="inline animate-spin mr-2" />
                Loading...
              </div>
            ) : null
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
